import { Drug } from '../Drug';
import { Method } from '../Method';
import { Target } from '../Target';
import { DrugModel } from '../DrugModel';
import { ClearanceMethods } from '../utils/constants';
import { Delivery } from '../Delivery';

export const drug_models: Drug[] = [
  new Drug({
    id: 'VANC',
    name: 'Vancomycin',
    class: 'Glycopeptides',
    dosingUnit: 250,
    maxDosePerHr: 600,
    methods: [
      new Method({
        type: 'IV',
        intervalRange: [6, 8, 12, 24],
        intervalUnit: 'hours',
        amountRange: [500, 750, 1000, 1250, 1500, 1750, 2000],
        amountUnit: 'mg',
        infusionRange: [1, 1.5, 2, 2.5, 3, 3.5, 4],
        infusionUnit: 'hours',
        simulationGuidance:
          'Regimens highlighted in green achieve the specific target concentration. Doses above 2000mg as a short-term infusion are not recommended.',
        standardDoses: [
          // 7 dose/infusion pairs × 4 intervals = 28 combinations
          ...([6, 8, 12, 24] as number[]).flatMap((interval) => [
            { amount: 500, infusionTime: 1, interval },
            { amount: 750, infusionTime: 1.5, interval },
            { amount: 1000, infusionTime: 2, interval },
            { amount: 1250, infusionTime: 2.5, interval },
            { amount: 1500, infusionTime: 2.5, interval },
            { amount: 1750, infusionTime: 3, interval },
            { amount: 2000, infusionTime: 3.5, interval },
          ]),
        ],
      }),
      new Method({
        type: 'CONT',
        intervalRange: [],
        intervalUnit: 'h',
        amountRange: [2000, 2500, 3000, 3500, 4000, 4500, 5000],
        amountUnit: 'mg',
      }),
      // {
      //     //Oral vancomycin is currently available as 125 mg and 250 mg capsules and 250 mg / 5 mL oral solution. It is typically administered four times a day for 7 to 10 days.
      //     //Clearance: 0.71 mL/minute/kg to 1.31 mL/minute/kg in adults with normal renal function
      //     type: 'ORAL',
      //     amountRange: [125, 250],
      //     amountUnit: 'mg',
      //     intervalRange: [6, 8],
      //     intervalUnit: 'h',
      //     bioavailability: 0.1,
      //     halflife: 0.5,
      // }
    ],
    target: [
      // new Target({
      //     type: 'AUC',
      //     min: 400,
      //     max: 600,
      //     unit: 'mg*hr/L',
      //     range: [100, 700],
      //     href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5740375/'
      // }),
      new Target({
        type: 'AUC',
        min: 400,
        max: 600,
        unit: 'mg·hr/L',
        range: [100, 700],
        href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5740375/',
      }),
      new Target({
        type: 'Peak',
        min: 30,
        max: 40,
        unit: 'mg/L',
        range: [10, 60],
        href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5740375/',
      }),
      new Target({
        type: 'Trough',
        min: 5,
        max: 10,
        unit: 'mg/L',
        range: [1, 30],
        href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5341531/',
      }),
      new Target({
        type: 'Steady state',
        min: 5,
        max: 10,
        unit: 'mg/L',
        range: [1, 100],
        href: '',
      }),
    ],
    models: [
      new DrugModel({
        code: 'BUEL2005',
        name: 'General: Buelga 2005',
        info: [
          'General model for Vancomycin',
          'CL(Vanc)(L/h) = 1.08 × CLCR (Cockcroft-Gault modified) (mL/min)',
          'Vd (L) = 0.98 × TBW',
        ],
        drug: 'Vancomycin',
        href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1315926/',
        delivery: new Delivery({
          method: 'IV',
          // interval: 12,
          // intervalUnit: 'hours',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: {
          range: [15, 99],
          unit: 'years',
        },
        weight: {
          range: [40, 100],
          unit: 'kg',
        },
        default: {
          initialDose: 1000,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 20, // 20-25mg/kg
          maintenanceDoseMgPerKg: 15, // 15-20mg/kg
          minDose: 50,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 2500, // mg
          capCrcl: 120,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044, // per hour
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 1.08,
            sd: 0.2816,
            unit: 'L/hr',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 0.98,
            sd: 0.3715,
            unit: 'L/kg',
          },
        ],
      }),
      new DrugModel({
        code: 'THOM2009',
        name: 'General: Thomson 2009',
        info: [
          '2-compartment model for general adult hospital patients',
          'CL (L/h) = 2.99 × (1 + 0.0154 × (CrCL − 66)) where CrCL from Cockcroft-Gault (mL/min)',
          'V1 (L) = 0.675 × TBW, V2 (L) = 0.732 × TBW, Q = 2.28 L/h',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1093/jac/dkp085',
        delivery: new Delivery({
          method: 'IV',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: {
          range: [16, 97],
          unit: 'years',
        },
        weight: {
          range: [40, 159],
          unit: 'kg',
        },
        default: {
          initialDose: 1000,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 20,
          maintenanceDoseMgPerKg: 15,
          minDose: 50,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 2500,
          capCrcl: 120,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044,
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'central',
            coeff: 2.99,
            sd: 0.27,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
          {
            type: 'intercompartmental',
            coeff: 2.28,
            sd: 0,
            unit: 'L/h',
          },
        ],
        vd: [
          {
            type: 'central',
            coeff: 0.675,
            sd: 0.15,
            unit: 'L/kg',
          },
          {
            type: 'peripheral',
            coeff: 0.732,
            sd: 1.3,
            unit: 'L/kg',
          },
        ],
      }),
      new DrugModel({
        code: 'LLOP2006',
        name: 'Critically Ill: Llopis-Salvia 2006',
        info: [
          '2-compartment model for adult ICU/critically ill patients',
          'CL (L/h) = 0.034 × CrCL (mL/min) + 0.015 × TBW (kg)',
          'Vc (L) = 0.414 × TBW, Vp (L) = 1.32 × TBW, Q = 7.48 L/h',
          'Accounts for elevated non-renal clearance in critically ill patients',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1111/j.1365-2710.2006.00762.x',
        delivery: new Delivery({
          method: 'IV',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult ICU',
        age: {
          range: [18, 81],
          unit: 'years',
        },
        weight: {
          range: [40, 130],
          unit: 'kg',
        },
        default: {
          initialDose: 1000,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 20,
          maintenanceDoseMgPerKg: 15,
          minDose: 50,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 2500,
          capCrcl: 120,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044,
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'central',
            coeff: 0.034,
            sd: 0.292,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
          {
            type: 'intercompartmental',
            coeff: 7.48,
            sd: 0,
            unit: 'L/h',
          },
        ],
        vd: [
          {
            type: 'central',
            coeff: 0.414,
            sd: 0.364,
            unit: 'L/kg',
          },
          {
            type: 'peripheral',
            coeff: 1.32,
            sd: 0.398,
            unit: 'L/kg',
          },
        ],
      }),
      new DrugModel({
        code: 'GOTI2018',
        name: 'Hospitalized ± Dialysis: Goti 2018',
        info: [
          '2-compartment model for hospitalized adults with or without hemodialysis',
          'CL (L/h) = 4.5 × (CrCL/120)^0.8 × 0.7^DIAL; CrCL capped at 150 mL/min',
          'Vc (L) = 58.4 × (WT/70) × 0.5^DIAL; Vp = 38.4 L; Q = 6.5 L/h',
          'DIAL=1 for hemodialysis patients (halves Vc, reduces CL by ~35%)',
          'SCr truncated to 1 mg/dL for patients aged > 60 years',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1097/FTD.0000000000000448',
        delivery: new Delivery({
          method: 'IV',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        usesDialysis: true,
        population: 'Adult Hospitalized ± Hemodialysis',
        age: {
          range: [16, 101],
          unit: 'years',
        },
        weight: {
          range: [33, 255],
          unit: 'kg',
        },
        default: {
          initialDose: 1000,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 25,
          maintenanceDoseMgPerKg: 15,
          minDose: 50,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 3000,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044,
          interval: (crcl: number) => {
            if (crcl > 100) return 12;
            else if (crcl >= 60) return 12;
            else return 24;
          },
        },
        clearance: [
          {
            type: 'central',
            coeff: 4.5,
            sd: 0.398,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
          {
            type: 'intercompartmental',
            coeff: 6.5,
            sd: 0.163,
            unit: 'L/h',
          },
        ],
        vd: [
          {
            type: 'central',
            coeff: 0.834, // 58.4 / 70 — formula is 58.4 × (WT/70); DIAL multiplier (×0.5) applied in DynamicSimulation
            sd: 0.816,
            unit: 'L/kg',
          },
          {
            type: 'peripheral',
            coeff: 38.4,
            sd: 0.571,
            unit: 'L',
          },
        ],
      }),
      new DrugModel({
        code: 'ADAN2015',
        name: 'Extremely Obese: Adane 2015',
        info: [
          '1-compartment model for extremely obese adults (BMI ≥ 40 kg/m², weight ≥ 120 kg)',
          'CL (L/h) = 6.54 × (CrCL_BSA / 125), CrCL via Cockcroft-Gault BSA-normalised (mL/min/1.73m²)',
          'Vd (L) = 0.51 × TBW',
          'Note: app uses ADJWT (not TBW) for CrCL — population PK is subsequently refined by Bayesian estimation',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1002/phar.1531',
        delivery: new Delivery({
          method: 'IV',
          duration: 1.5,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult Extremely Obese',
        age: {
          range: [18, 80],
          unit: 'years',
        },
        weight: {
          range: [120, 300],
          unit: 'kg',
        },
        default: {
          initialDose: 2000,
          initialInfusionDuration: 1.5,
          loadingDoseMgPerKg: 20,
          maintenanceDoseMgPerKg: 15,
          minDose: 50,
          maxDose: 7000,
          incrementDose: 250,
          capDose: 3000,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044,
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 6.54,
            sd: 0.267,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 0.51,
            sd: 0.239,
            unit: 'L/kg',
          },
        ],
      }),
      new DrugModel({
        code: 'MASI2020',
        name: 'Obese Sepsis: Masich 2020',
        info: [
          '1-compartment model for obese adults (BMI ≥ 30 kg/m²) with sepsis or septic shock',
          'CL (L/h) = 6.88 × (CrCL / 40)^0.69, CrCL via Cockcroft-Gault with ADJWT (mL/min)',
          'Vc = 85 L (fixed, weight-independent)',
          'n=16; power-law exponent 95% CI 0.45–0.92; 69% of study patients had acute kidney injury',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1002/phar.2367',
        delivery: new Delivery({
          method: 'IV',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult Obese Sepsis',
        age: {
          range: [18, 80],
          unit: 'years',
        },
        weight: {
          range: [60, 200],
          unit: 'kg',
        },
        default: {
          initialDose: 1500,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 20,
          maintenanceDoseMgPerKg: 15,
          minDose: 50,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 2500,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044,
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 6.88,
            sd: 0.315,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 85.0,
            sd: 0.124,
            unit: 'L',
          },
        ],
      }),
      new DrugModel({
        code: 'ROBERTS2011',
        name: 'Critically Ill: Roberts 2011',
        info: [
          'Critically ill ICU patient model for Vancomycin',
          'BMI < 30 kg/m2',
          'CL(Vanc)(L/h) = 4.58 * (CrCL (Cockcroft-Gault (mL/min)) / 1.73 (m2) * BSA (m2)) / 100',
          'Vd (L) = 1.53 × TBW',
        ],
        drug: 'Vancomycin',
        href: 'https://doi.org/10.1128/aac.01708-10',
        delivery: new Delivery({
          method: 'IV',
          // interval: 12,
          // intervalUnit: 'hours',
          duration: 1,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: {
          range: [18, 99],
          unit: 'years',
        },
        weight: {
          range: [40, 100],
          unit: 'kg',
        },
        default: {
          initialDose: 1000,
          initialInfusionDuration: 1,
          loadingDoseMgPerKg: 35,
          maintenanceDoseMgPerKg: 35,
          minDose: 500,
          maxDose: 5000,
          incrementDose: 250,
          capDose: 2500, // mg
          capCrcl: 120,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044, // per hourrate
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 4.58,
            sd: 0.389,
            unit: 'L/hr',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 1.53,
            sd: 0.374,
            unit: 'L/kg',
          },
        ],
      }),
    ],
  }),
  new Drug({
    id: 'GENT',
    name: 'Gentamicin',
    class: 'Aminoglycosides',
    dosingUnit: 20,
    methods: [
      new Method({
        type: 'IV',
        intervalRange: [24, 36, 48],
        intervalUnit: 'hours',
        amountRange: [80, 120, 160, 200, 240, 280, 320, 360, 400, 480],
        amountUnit: 'mg',
        infusionRange: [0.5, 1],
        infusionUnit: 'hours',
        simulationGuidance:
          'Once-daily dosing: administer over 30–60 minutes. Target Cmax 8–12 mg/L and trough <1 mg/L.',
        standardDoses: [
          ...([24, 36, 48] as number[]).flatMap((interval) => [
            { amount: 80, infusionTime: 0.5, interval },
            { amount: 120, infusionTime: 0.5, interval },
            { amount: 160, infusionTime: 0.5, interval },
            { amount: 200, infusionTime: 0.5, interval },
            { amount: 240, infusionTime: 0.5, interval },
            { amount: 280, infusionTime: 0.5, interval },
            { amount: 320, infusionTime: 0.5, interval },
            { amount: 360, infusionTime: 0.5, interval },
            { amount: 400, infusionTime: 0.5, interval },
            { amount: 480, infusionTime: 0.5, interval },
          ]),
        ],
      }),
    ],
    target: [
      new Target({
        type: 'Peak',
        min: 8,
        max: 12,
        unit: 'mg/L',
        range: [1, 30],
        href: 'https://doi.org/10.1371/journal.pone.0177324',
      }),
      new Target({
        type: 'Trough',
        min: 0,
        max: 1,
        unit: 'mg/L',
        range: [0, 5],
        href: 'https://doi.org/10.1371/journal.pone.0177324',
      }),
    ],
    models: [
      new DrugModel({
        code: 'GENTA_STD',
        name: 'General Ward: Gomes 2017',
        info: [
          'Standard ward model for Gentamicin (Dutch national model)',
          'CL (L/h) = 0.225 × (TBW/70) + 0.727 × CrCl (L/h, Cockcroft-Gault)',
          'Vd (L) = 0.273 × IBW (kg)',
          'Reference: Gomes et al. PLoS One 2017 (doi:10.1371/journal.pone.0177324)',
        ],
        drug: 'Gentamicin',
        href: 'https://doi.org/10.1371/journal.pone.0177324',
        delivery: new Delivery({
          method: 'IV',
          duration: 0.5,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: { range: [18, 99], unit: 'years' },
        weight: { range: [40, 150], unit: 'kg' },
        default: {
          loadingDoseMgPerKg: 5,
          maintenanceDoseMgPerKg: 5,
          minDose: 20,
          maxDose: 600,
          incrementDose: 20,
          capCrcl: 120,
          kel: (crcl: number) => (0.003 * crcl) / 70 + 0.01,
          interval: (crcl: number) => (crcl > 60 ? 24 : crcl >= 40 ? 36 : 48),
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 0.225,
            sd: 0.6,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
          {
            type: 'renal-fraction',
            coeff: 0.727,
            sd: 0,
            unit: 'fraction',
            method: null,
            methodUnit: null,
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 0.273,
            sd: 0.3,
            unit: 'L/kg IBW',
          },
        ],
      }),
      new DrugModel({
        code: 'GENTA_ICU',
        name: 'ICU: Gomes 2017',
        info: [
          'ICU model for Gentamicin (Dutch national model)',
          'CL (L/h) = 0.277 × (TBW/70) + 0.899 × CrCl (L/h, Cockcroft-Gault)',
          'Vd (L) = 0.335 × IBW (kg)',
          'Reference: Gomes et al. PLoS One 2017 (doi:10.1371/journal.pone.0177324)',
        ],
        drug: 'Gentamicin',
        href: 'https://doi.org/10.1371/journal.pone.0177324',
        delivery: new Delivery({
          method: 'IV',
          duration: 0.5,
          durationUnit: 'hours',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: { range: [18, 99], unit: 'years' },
        weight: { range: [40, 150], unit: 'kg' },
        default: {
          loadingDoseMgPerKg: 5,
          maintenanceDoseMgPerKg: 5,
          minDose: 20,
          maxDose: 600,
          incrementDose: 20,
          capCrcl: 120,
          kel: (crcl: number) => (0.003 * crcl) / 70 + 0.01,
          interval: (crcl: number) => (crcl > 60 ? 24 : crcl >= 40 ? 36 : 48),
        },
        clearance: [
          {
            type: 'one-compartment',
            coeff: 0.277,
            sd: 0.43,
            unit: 'L/h',
            method: ClearanceMethods.SeCR,
            methodUnit: 'umol/L',
          },
          {
            type: 'renal-fraction',
            coeff: 0.899,
            sd: 0,
            unit: 'fraction',
            method: null,
            methodUnit: null,
          },
        ],
        vd: [
          {
            type: 'one-compartment',
            coeff: 0.335,
            sd: 0.31,
            unit: 'L/kg IBW',
          },
        ],
      }),
    ],
  }),
  new Drug({
    id: 'PIP',
    name: 'Piperacillin',
    class: 'Penicillin class of beta-lactams',
    methods: [
      new Method({
        type: 'IV',
        intervalRange: [6, 8, 12, 24],
        intervalUnit: 'hours',
        amountRange: [2000, 3000, 4000],
        amountUnit: 'mg',
        infusionRange: [0.5, 1, 1.5, 2, 2.5],
        infusionUnit: 'hours',
      }),
      new Method({
        type: 'CONT',
        intervalRange: [],
        intervalUnit: 'h',
        amountRange: [
          2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000, 8500, 9000,
          9500, 10000,
        ],
        amountUnit: 'mg',
      }),
    ],
    target: [
      new Target({
        type: 'fT>MIC',
        min: 50,
        max: 100,
        unit: '%',
        range: [1, 100],
        href: 'https://pubmed.ncbi.nlm.nih.gov/32383061/',
      }),
    ],
    models: [
      new DrugModel({
        code: 'UDY2015',
        name: 'General: Udy 2015',
        info: [
          'General model for Piperacillin',
          '[Pip] CL(L/h) = 16.3 × CLCR(urinary, mL/min)/100',
        ],
        drug: 'Piperacillin',
        href: 'https://pubmed.ncbi.nlm.nih.gov/25632974/',
        delivery: new Delivery({
          method: 'IV',
          duration: 20,
          durationUnit: 'minutes',
          daysToDisplay: 3,
        }),
        population: 'Adult',
        age: {
          range: [18, 80],
          unit: 'years',
        },
        weight: {
          range: [40, 100],
          unit: 'kg',
        },
        default: {
          minDose: 1000,
          maxDose: 6000,
          kel: (crcl: number) => 0.00083 * crcl + 0.0044, // per hour
          interval: (crcl: number) => {
            if (crcl > 100) {
              return 8;
            } else if (crcl >= 60 && crcl <= 100) {
              return 12;
            } else {
              return 24;
            }
          },
        },
        clearance: [
          {
            type: 'central',
            coeff: 16.3,
            sd: 0.56,
            unit: 'L/h',
            method: ClearanceMethods.CLCR,
            methodUnit: 'L/h',
          },
          {
            type: 'intercompartmental',
            coeff: 37.3,
            sd: 0,
            unit: 'L/h',
          },
        ],
        vd: [
          {
            type: 'central',
            coeff: 19.9,
            sd: 0.296,
            unit: 'L',
          },
          {
            type: 'peripheral',
            coeff: 18.8,
            sd: 0.676,
            unit: 'L',
          },
        ],
      }),
    ],
  }),
];
